/**
 * The blackjack room's three Train cards, opened (Phase 8, box 4).
 *
 * ================================ WHAT EXISTED ================================
 * Nothing that ran. The Train tab drew three cards marked "Being built — Phase 8",
 * the engine had everything a drill needs — `basicAction` (the chart, checked cell by
 * cell against the published table), `decide` / `costOf` (the exact analysis), a
 * table's `composition()`, `snapshot` / `rehydrate` — and no screen used any of it
 * outside the live table. So this file is small on purpose: three drills, each a
 * thin screen over engine functions that already had tests, and no new arithmetic.
 *
 *   Basic Strategy Drill   a fresh-shoe hand and an up-card; graded against the chart,
 *                          with what the exact play costs shown beside it.
 *   Count Drill            a five-seat hand played out and flashed a card at a time;
 *                          the answer is the Hi-Lo count of what `composition()` says
 *                          left the shoe. An exercise, and it says so: the room
 *                          reshuffles before every hand.
 *   Decision Spots         `bj-spots.ts`: real deals where the cards already on the
 *                          table move the exact answer off the chart.
 *
 * PHASE 9 (master plan §9): the Count Drill, Decision Spots and the exact numbers in
 * the Basic Strategy Drill are Blackjack Pro — $9.99 once, or included in Poker Pro.
 * The Basic Strategy Drill itself stays free, graded against the chart.
 * ==============================================================================
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PlayingCard } from "../components/PlayingCard";
import { MoneyProvider, useMoney } from "../lib/MoneyContext";
import { CHIP_VALUE, inDollars } from "@engine/blackjack/money";
import type { Action } from "@engine/blackjack/table";
import { DOUBLE_DECK_S17, rankSlot } from "@engine/blackjack/rules";
import { basicAction } from "@engine/blackjack/bots";
import { costOf, INDIFFERENCE, type Decision } from "@engine/blackjack/decide";
import { rehydrate, type TableSnapshot } from "@engine/blackjack/snapshot";
import { analyse } from "../lib/analyst";
import { totalLabel } from "../table/blackjack-view";
import { BJ_TRAIN_CARDS } from "./bj-copy";
import { BJ_SPOTS } from "./bj-spots";
import { freshSpot, runningCount, countHand } from "./bj-drills";
import { useUnlocks } from "../lib/useUnlocks";
import { money } from "../lib/api";
import { UNLOCKS } from "@config/plans";
import "./bj-drills.css";

const RULES = DOUBLE_DECK_S17;
const MOVE_WORD: Record<Action, string> = { hit: "Hit", stand: "Stand", double: "Double", split: "Split" };
const verb = (a: string) => (a === "decline" ? "declining" : a === "insurance" ? "insurance" : a);

/* ---------------------------------------------------------------- shared --- */

/** A hand at the drill's felt: one row of the room's own card. */
function Hand({ cards, label, hero }: { cards: readonly number[]; label: string; hero?: boolean }) {
  return (
    <div className={"bjd-hand" + (hero ? " bjd-hero" : "")}>
      <div className="bjd-cards">
        {cards.map((c, i) => <PlayingCard key={i} kind={hero ? "bj-hero" : "bj"} card={c} />)}
      </div>
      <p className="bjd-label">{label}</p>
    </div>
  );
}

/** "an ace", "a six" — the way a table says an up-card. */
const UP_WORD = ["two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "jack", "queen", "king", "ace"];
const upWord = (c: number) => { const w = UP_WORD[c >> 2]!; return `${/^[ae]/.test(w) ? "an" : "a"} ${w}`; };

/** The exact numbers for a position, off-thread, as the live table gets them. */
function useDecision(snap: TableSnapshot | null, ask: boolean): Decision | null | "working" {
  const [d, setD] = useState<Decision | null | "working">(null);
  useEffect(() => {
    if (!snap || !ask) { setD(null); return; }
    let on = true;
    setD("working");
    void analyse(snap).result.then((r) => { if (on) setD(r.decision); }).catch(() => { if (on) setD(null); });
    return () => { on = false; };
  }, [snap, ask]);
  return d;
}

function EvList({ d, took }: { d: Decision; took: string }) {
  const { fmt } = useMoney();
  return (
    <ul className="bj-evs bjd-evs">
      {d.actions.map((a) => (
        <li key={a.action} className={a.action === took ? "took" : d.best === a.action ? "best" : ""}>
          <span className="bj-ev-name">{MOVE_WORD[a.action as Action] ?? a.action}</span>
          <span className="num">{a.ev >= 0 ? "+" : "−"}{fmt(Math.abs(Math.round(inDollars(a.ev) * 100) / 100))}</span>
          {a.action === took ? <em>your choice</em> : d.best === a.action ? <em>best</em> : null}
        </li>
      ))}
    </ul>
  );
}

function Moves({ legal, onPick, disabled }: { legal: readonly Action[]; onPick: (a: Action) => void; disabled?: boolean }) {
  return (
    <div className="bjd-moves">
      {(["hit", "stand", "double", "split"] as Action[]).map((a) => (
        <button key={a} type="button" className={`btn bjd-${a}`} disabled={disabled || !legal.includes(a)}
                onClick={() => onPick(a)}>{MOVE_WORD[a]}</button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------ basic strategy --- */

function BasicDrill({ exact, loading }: { exact: boolean; loading: boolean }) {
  const { fmt } = useMoney();
  const [snap, setSnap] = useState<TableSnapshot>(freshSpot);
  const [took, setTook] = useState<Action | null>(null);
  const [tally, setTally] = useState({ n: 0, right: 0 });
  const t = useMemo(() => rehydrate(snap, RULES), [snap]);
  const cards = snap.seats[0]!.hands[0]!.cards;
  const up = snap.dealer[0]!;
  const legal = t.legalActions();
  const chart = basicAction(cards, rankSlot(up >> 2), legal);
  const d = useDecision(snap, took !== null && exact);

  const pick = (a: Action) => {
    if (took) return;
    setTook(a);
    setTally((x) => ({ n: x.n + 1, right: x.right + (a === chart ? 1 : 0) }));
  };
  const next = () => { setTook(null); setSnap(freshSpot()); };

  return (
    <section className="bjd">
      <div className="bjd-felt">
        <Hand cards={[up]} label={`Dealer shows ${upWord(up)}`} />
        <Hand cards={cards} label={`You: ${totalLabel(cards)}`} hero />
      </div>
      <Moves legal={legal} onPick={pick} disabled={took !== null} />
      {took && (
        <div className="bjd-answer" role="status">
          <p className={took === chart ? "bjd-ok" : "bjd-no"}>
            {took === chart ? "That is the chart's play." : `The chart says ${verb(chart)}.`}
          </p>
          {!exact && !loading && (
            <p className="muted small">
              What the exact play was, and what your choice cost against it, is{" "}
              <Link to="/pricing">Blackjack Pro</Link>.
            </p>
          )}
          {exact && d === "working" && <p className="muted small">Working out the deck…</p>}
          {exact && d && d !== "working" && (() => {
            const cost = costOf(d, took);
            return (
              <>
                <p className="muted small">
                  Exact — computed from the deck: <b>{verb(d.best)}</b>.{" "}
                  {cost <= INDIFFERENCE
                    ? "Your choice gave up nothing that matters."
                    : <>Your choice cost <span className="num">{fmt(Math.round(inDollars(cost) * 100) / 100)}</span> on a {fmt(CHIP_VALUE)}-Buck bet.</>}
                  {d.best !== chart && " Here the two cards out of the deck move the answer off the chart."}
                </p>
                <EvList d={d} took={took} />
              </>
            );
          })()}
          <button type="button" className="btn" onClick={next}>Next hand</button>
        </div>
      )}
      <p className="muted small bjd-tally">
        {tally.n === 0 ? "Say what the chart says." : `${tally.right} of ${tally.n} on the chart this run.`}
      </p>
    </section>
  );
}

/* ---------------------------------------------------------------- count --- */

const SPEEDS = [{ id: "slow", ms: 900 }, { id: "table", ms: 600 }, { id: "fast", ms: 350 }] as const;

function CountDrill() {
  const [hand, setHand] = useState(() => countHand());
  const [shown, setShown] = useState(0);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]["id"]>("table");
  const [guess, setGuess] = useState("");
  const [done, setDone] = useState(false);
  const [tally, setTally] = useState({ n: 0, right: 0 });
  const ms = SPEEDS.find((s) => s.id === speed)!.ms;
  const answer = useMemo(() => runningCount(hand.table), [hand]);
  /* `shown` counts cards turned so far; the last one is held for its own beat before the
     question. At `shown < length` the flash ended the moment the last card was reached,
     so the dealer's final card was counted in the answer and never shown. */
  const running = shown <= hand.cards.length;

  useEffect(() => {
    if (!running) return;
    const h = window.setTimeout(() => setShown((n) => n + 1), shown === 0 ? 400 : ms);
    return () => window.clearTimeout(h);
  }, [running, shown, ms]);

  const check = () => {
    if (done || guess.trim() === "") return;
    setDone(true);
    setTally((x) => ({ n: x.n + 1, right: x.right + (Number(guess) === answer ? 1 : 0) }));
  };
  const next = useCallback(() => { setHand(countHand()); setShown(0); setGuess(""); setDone(false); }, []);

  return (
    <section className="bjd">
      <p className="muted small">
        Hi-Lo, over one five-seat hand: two to six count one, seven to nine nothing, tens
        and aces minus one.
      </p>
      <div className="bjd-speed" role="group" aria-label="Speed">
        {SPEEDS.map((s) => (
          <button key={s.id} type="button" className={"btn-small" + (speed === s.id ? " is-on" : "")}
                  onClick={() => setSpeed(s.id)}>{s.id === "table" ? "Table pace" : s.id === "slow" ? "Slow" : "Fast"}</button>
        ))}
      </div>
      <div className="bjd-felt bjd-flash" aria-live="off">
        {shown > 0 && running && <PlayingCard key={shown} kind="bj-hero" card={hand.cards[shown - 1]!} />}
        {!running && !done && <p className="bjd-label">{hand.cards.length} cards. What is the count?</p>}
        {done && (
          <div className="bjd-cards bjd-all">
            {hand.cards.map((c, i) => <PlayingCard key={i} kind="bj" card={c} />)}
          </div>
        )}
      </div>
      {!running && (
        <form className="bjd-count" onSubmit={(e) => { e.preventDefault(); check(); }}>
          <label>
            <span>Running count</span>
            <input type="number" inputMode="numeric" value={guess} disabled={done}
                   onChange={(e) => setGuess(e.target.value)} />
          </label>
          {!done && <button className="btn" type="submit">Check</button>}
        </form>
      )}
      {done && (
        <div className="bjd-answer" role="status">
          <p className={Number(guess) === answer ? "bjd-ok" : "bjd-no"}>
            {Number(guess) === answer ? `Right — ${answer > 0 ? "+" : ""}${answer}.` : `The count is ${answer > 0 ? "+" : ""}${answer}.`}
          </p>
          <button type="button" className="btn" onClick={next}>Next hand</button>
        </div>
      )}
      <p className="muted small bjd-tally">{tally.n ? `${tally.right} of ${tally.n} this run.` : " "}</p>
    </section>
  );
}

/* ------------------------------------------------------------- the spots --- */

function SpotsDrill() {
  const [i, setI] = useState(0);
  const [took, setTook] = useState<Action | null>(null);
  const spot = BJ_SPOTS[i % BJ_SPOTS.length]!;
  const snap = spot.snap;
  const t = useMemo(() => rehydrate(snap, RULES), [snap]);
  const hero = snap.seats[0]!.hands[0]!.cards;
  const up = snap.dealer[0]!;
  const legal = t.legalActions();
  const chart = basicAction(hero, rankSlot(up >> 2), legal);
  const d = useDecision(snap, took !== null);
  const others = snap.seats.length - 1;

  return (
    <section className="bjd">
      <p className="bjd-q">
        Dealer shows {upWord(up)}, you hold {totalLabel(hero)}, {others} other players — what do you do?
      </p>
      <div className="bjd-felt bjd-table">
        <Hand cards={[up]} label="Dealer" />
        <div className="bjd-seats">
          {snap.seats.map((s, k) => (
            <Hand key={k} cards={s.hands[0]!.cards} label={k === 0 ? "You" : `Seat ${k + 1}`} hero={k === 0} />
          ))}
        </div>
      </div>
      <Moves legal={legal} onPick={(a) => !took && setTook(a)} disabled={took !== null} />
      {took && (
        <div className="bjd-answer" role="status">
          {d === "working" && <p className="muted small">Working out the deck…</p>}
          {d && d !== "working" && (
            <>
              <p className={costOf(d, took) <= INDIFFERENCE ? "bjd-ok" : "bjd-no"}>
                The exact play is <b>{verb(d.best)}</b>. The chart says {verb(chart)}.
              </p>
              <p className="muted small">
                The chart is written for a full shoe. With the {snap.seats.length * 2 - 2} cards
                in front of the other seats already out of it, the numbers below are this
                deck&rsquo;s, not the chart&rsquo;s, on a 25-Buck bet.
              </p>
              <EvList d={d} took={took} />
            </>
          )}
          <button type="button" className="btn" onClick={() => { setTook(null); setI((n) => n + 1); }}>
            Next spot
          </button>
        </div>
      )}
      <p className="muted small bjd-tally">Spot {(i % BJ_SPOTS.length) + 1} of {BJ_SPOTS.length}</p>
    </section>
  );
}

/* ------------------------------------------------------------------ route --- */

/** A Blackjack Pro drill, for somebody who does not hold it: what it is, and the door. */
function ProLock() {
  const u = UNLOCKS.blackjack_pro;
  return (
    <section className="bjd bjd-lock" data-td="bj-drill-lock">
      <p>
        This drill is part of <b>Blackjack Pro</b>: {money(u.amountCents)} once, or
        included in Poker Pro. It adds:
      </p>
      <ul>{u.grants.map((g) => <li key={g}>{g}</li>)}</ul>
      <p className="row">
        <Link className="btn" to="/pricing">See Blackjack Pro</Link>{" "}
        <Link className="btn-quiet" to="/blackjack/train/run?card=basic">The Basic Strategy Drill is free</Link>
      </p>
    </section>
  );
}

export function BlackjackTrainRun() {
  const [params] = useSearchParams();
  const card = BJ_TRAIN_CARDS.find((c) => c.id === params.get("card")) ?? BJ_TRAIN_CARDS[0]!;
  const unlocks = useUnlocks();
  const exact = unlocks.has("blackjack_pro");
  return (
    <MoneyProvider bigBlind={CHIP_VALUE} format="bucks" pin="bucks">
      <div className="bjd-page">
        <p className="bjd-back"><Link to="/blackjack/train">‹ Training</Link></p>
        <h1 className="display bjd-title">{card.title}</h1>
        <p className="muted">{card.line}</p>
        {card.id === "basic" ? <BasicDrill exact={exact} loading={unlocks.loading} />
          : unlocks.loading ? <p className="muted small">Checking your account…</p>
          : !exact ? <ProLock />
          : card.id === "count" ? <CountDrill /> : <SpotsDrill />}
      </div>
    </MoneyProvider>
  );
}
